import { useState } from 'react';
import { Box, Image, Text, HStack, Wrap, WrapItem } from '@chakra-ui/react';

export interface BlogPostListItemData {
  id: string;
  title: string;
  description: string;
  readTime: number;
  datePublished: string;
  tags: string[];
  coverPhotoUrl: string;
}

interface BlogPostListItemProps {
  post: BlogPostListItemData;
  /** When true, use stacked card layout; otherwise horizontal bar with image on the right. */
  isCard: boolean;
  timeAgo: string;
  onOpen: () => void;
}

/**
 * Single post entry in the Blogs list (menu) view.
 * - Wide (bar): text left, cover image right (flush to edge)
 * - Narrow (card): cover on top, then text + meta
 */
export function BlogPostListItem({ post, isCard, timeAgo, onOpen }: BlogPostListItemProps) {
  // Covers are preloaded before the list mounts; still hide until this instance is ready
  // so progressive JPEGs don't paint scanline-by-scanline.
  const [imageVisible, setImageVisible] = useState(false);

  const cover = (
    <Box
      className={
        isCard
          ? 'blog-post-list-item__media blog-post-list-item__media--card'
          : 'blog-post-list-item__media blog-post-list-item__media--bar'
      }
      flexShrink={0}
      overflow="hidden"
      bg="#1a1d24"
      m={0}
    >
      {post.coverPhotoUrl ? (
        <Image
          src={post.coverPhotoUrl}
          alt=""
          className="blog-post-list-item__img"
          w="100%"
          h="100%"
          minH="100%"
          objectFit="cover"
          objectPosition="center"
          display="block"
          borderRadius={0}
          loading="eager"
          decoding="async"
          opacity={imageVisible ? 1 : 0}
          transition="opacity 0.12s ease"
          onLoad={(event) => {
            const img = event.currentTarget as HTMLImageElement;
            const reveal = () => setImageVisible(true);
            if (typeof img.decode === 'function') {
              img.decode().then(reveal).catch(reveal);
            } else {
              reveal();
            }
          }}
          ref={(node) => {
            // Cached images may already be complete without a new load event.
            const img = node as HTMLImageElement | null;
            if (img?.complete && img.naturalWidth > 0 && !imageVisible) {
              setImageVisible(true);
            }
          }}
          onError={() => setImageVisible(false)}
        />
      ) : (
        <Box
          w="100%"
          h="100%"
          minH="120px"
          bg="#1a1d24"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text fontSize="sm" color="#6e7ba0" fontFamily="Roboto, sans-serif">
            No image
          </Text>
        </Box>
      )}
    </Box>
  );

  const titleBlock = (
    <Text
      fontSize={isCard ? '1.05rem' : '1.125rem'}
      fontWeight="bold"
      fontFamily="Roboto, sans-serif"
      color="#e9ebf1"
      lineHeight="1.35"
      noOfLines={isCard ? 3 : 2}
      m={0}
      mb={2}
    >
      {post.title}
    </Text>
  );

  const descriptionBlock = (
    <Text
      fontSize="0.95rem"
      fontWeight="normal"
      fontFamily="Roboto, sans-serif"
      color="#6e7ba0"
      lineHeight="1.45"
      noOfLines={isCard ? 4 : 3}
      m={0}
      mb={2}
    >
      {post.description}
    </Text>
  );

  const relativeDate = (
    <Text fontSize="0.75rem" fontFamily="Roboto, sans-serif" color="#9fa8c1" m={0} lineHeight="1.3">
      {timeAgo}
    </Text>
  );

  const metaRow = (
    <HStack
      className="blog-post-list-item__meta"
      spacing={3}
      flexWrap="wrap"
      alignItems="center"
      rowGap={1}
      w="100%"
    >
      <Text
        fontSize="0.75rem"
        fontFamily="Roboto, sans-serif"
        color="#9fa8c1"
        m={0}
        whiteSpace="nowrap"
        flexShrink={0}
      >
        {post.readTime} min read
      </Text>
      <Wrap spacing="12px" shouldWrapChildren>
        {post.tags.map((tag, index) => (
          <WrapItem key={`${post.id}-tag-${index}`}>
            <Text
              fontSize="0.75rem"
              fontFamily="Roboto, sans-serif"
              color="#717684"
              m={0}
              lineHeight="1.2"
            >
              {tag}
            </Text>
          </WrapItem>
        ))}
      </Wrap>
    </HStack>
  );

  const itemBorder = '1px solid #3d4555';
  const itemBorderHover = '#5a6578';

  if (isCard) {
    return (
      <Box
        as="button"
        type="button"
        className="blog-post-list-item blog-post-list-item--card"
        w="100%"
        textAlign="left"
        border={itemBorder}
        borderRadius="8px"
        overflow="hidden"
        bg="#12151c"
        cursor="pointer"
        p={0}
        m={0}
        onClick={onOpen}
        _hover={{ borderColor: itemBorderHover, bg: '#151922' }}
        transition="border-color 0.15s ease, background 0.15s ease"
      >
        {cover}
        <Box className="blog-post-list-item__body" px="18px" py="16px">
          {titleBlock}
          {descriptionBlock}
          {relativeDate}
          <Box borderTop="1px solid #2a3040" pt="6px" mt="8px">
            {metaRow}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      as="button"
      type="button"
      className="blog-post-list-item blog-post-list-item--bar"
      w="100%"
      textAlign="left"
      border={itemBorder}
      borderRadius="6px"
      overflow="hidden"
      bg="transparent"
      cursor="pointer"
      display="flex"
      flexDirection="row"
      alignItems="stretch"
      minH="148px"
      p={0}
      m={0}
      gap={0}
      onClick={onOpen}
      _hover={{ borderColor: itemBorderHover, bg: 'rgba(18, 21, 28, 0.6)' }}
      transition="border-color 0.15s ease, background 0.15s ease"
    >
      <Box
        className="blog-post-list-item__body"
        flex="1 1 auto"
        minW={0}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        px="20px"
        py="16px"
      >
        <Box>
          {titleBlock}
          {descriptionBlock}
          {relativeDate}
        </Box>
        <Box borderTop="1px solid #2a3040" pt="6px" mt="8px">
          {metaRow}
        </Box>
      </Box>
      {cover}
    </Box>
  );
}

export default BlogPostListItem;
